/**
 * AdminListView (P15.1 / #416).
 *
 * Generic admin page wrapper. Concrete entity pages (VendorAdminPage,
 * IBSAdminPage, ...) pass their `EntityAdapter` here and inherit:
 *   - admin gate (non-admin sees a 403 panel)
 *   - paginated list (limit=100 + "Load more" button)
 *   - client-side search (text match on adapter.rowSearchHaystack)
 *   - client-side column sort (adapter.columns with sortValue)
 *   - drawer wiring (?edit=<id> / ?create=1) via URL search params
 *
 * Module-boundary rule: imports from `foundation/*` + `shared/*`
 * only.
 */
import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import type { ApiError } from "shared/api/client";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { EmptyState } from "shared/components/EmptyState";
import { useMe } from "shared/api/useMe";

import { AdminEntityDrawer } from "./AdminEntityDrawer";
import type { EntityAdapter } from "./types";
import { adminListQueryOptions } from "./useFoundationAdminList";

const PAGE_SIZE = 100;

export interface AdminListViewProps<TRow, TIn, TPatch> {
  adapter: EntityAdapter<TRow, TIn, TPatch>;
}

export function AdminListView<TRow, TIn, TPatch>({
  adapter,
}: AdminListViewProps<TRow, TIn, TPatch>) {
  const { t } = useTranslation();
  const me = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [sort, setSort] = useState(adapter.defaultSort);

  // Round 10 C3: a register may name its own authors -- a risk
  // appetite is the CRO's artefact, not the platform operator's.
  const isAdmin = adapter.canWrite
    ? adapter.canWrite(me.data)
    : (me.data?.is_admin ?? false);

  /* ROUND 13 C5 -- EVERY page that has been loaded, not the first and
     the most recent.
     
     The old comment here said intermediate pages "are still cached if
     previously loaded ... so this is fine". They were cached and they
     were not RENDERED: pressing Load more twice showed rows 1-100 and
     201-300 with 101-200 gone and unreachable without a reload, while
     the chip still read 200. On a register holding exactly 200 rows the
     second press REDUCED the screen from 200 rows to 100.
     
     `useQueries` asks for each page it has, so the list only grows. */
  /* Round 13 C7 -- the box asks the SERVER. Filtering the hundred rows
     already fetched made "No services match" a claim about the bank
     drawn from the contents of one page. Debounced, and it resets the
     paging: a search is a new question, not more of the old one. */
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  useEffect(() => {
    setPagesLoaded(1);
  }, [debouncedSearch]);

  const pages = useQueries({
    queries: Array.from({ length: pagesLoaded }, (_, i) =>
      adminListQueryOptions(adapter, {
        offset: i * PAGE_SIZE,
        limit: PAGE_SIZE,
        q: debouncedSearch,
      }),
    ),
  });
  const firstPage = pages[0]!;
  const lastPage = pages[pages.length - 1]!;

  const allRows = useMemo<TRow[]>(
    () => pages.flatMap((p) => p.data?.rows ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages.map((p) => p.dataUpdatedAt).join(","), pagesLoaded],
  );

  /* Round 13 C4 -- what the REGISTER holds, beside what the screen
     shows. The chip read "100 CIs" against a database holding 20,000,
     and nothing qualified the number, so the register stated a count of
     the bank. Null when the server did not say, which is not zero. */
  const estateTotal = firstPage.data?.total ?? null;

  /* The client-side filter STAYS, and deliberately. It narrows the rows
     already on screen while the debounce is in flight, so typing does
     not flash the whole register back. The server is what decides
     whether a row exists; this only decides what is drawn. */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allRows;
    return allRows.filter((row) =>
      adapter.rowSearchHaystack(row).toLowerCase().includes(needle),
    );
  }, [allRows, search, adapter]);

  const sorted = useMemo(() => {
    const col = adapter.columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return filtered;
    const sortValue = col.sortValue;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sort, adapter.columns]);

  const editId = searchParams.get("edit") ?? undefined;
  const creating = searchParams.get("create") === "1";

  const openCreate = () => {
    const next = new URLSearchParams(searchParams);
    next.set("create", "1");
    next.delete("edit");
    setSearchParams(next, { replace: false });
  };
  const openEdit = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("edit", id);
    next.delete("create");
    setSearchParams(next, { replace: false });
  };
  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  if (me.isLoading) {
    return (
      <p data-testid="admin-list-loading" className="text-sm text-slate-500">
        {t(`${adapter.i18nPrefix}.list.loading`)}
      </p>
    );
  }

  /*
   * Round 10 C2 / C3 -- who may READ a register of the bank's own
   * estate.
   *
   * This refused anybody without the admin role, for four rounds, on
   * registers the SERVER had already opened: `/ibses` admits the Head
   * of Operational Resilience (`require_opsres_or_admin`), data assets
   * and risk-appetite statements admit every seat a ceremony can wait
   * on (`require_ceremony_reader`). The page was refusing people the
   * API would have served -- so the Resilience Lead was told she needed
   * an administrator to read the estate she owns, and the journey spine
   * sent the CRO to a "Go →" that dead-ended on the same sentence.
   *
   * The gate is NOT mirrored here. A copy of a server rule is a copy
   * that drifts, and this whole defect is what drift looks like: the
   * server moved and the page did not. The read is attempted and the
   * SERVER's answer is rendered -- a 403 becomes the refusal, anything
   * else becomes the register.
   *
   * Writes stay admin, on both sides.
   */
  const firstError = firstPage.error as ApiError | null;
  const refused = firstError?.status === 403;
  if (refused) {
    return (
      <article className="max-w-3xl">
        <h1 className="text-2xl font-semibold mb-2">
          {t(`${adapter.i18nPrefix}.list.heading`)}
        </h1>
        <p
          data-testid="admin-list-forbidden"
          className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-4"
        >
          {t(`${adapter.i18nPrefix}.list.forbidden`)}
        </p>
      </article>
    );
  }

  const onSortClick = (key: string) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  /* Round 14 E4 -- from the register's own total where the server
     gives one. "A full last page means there is more" is wrong exactly
     at a page boundary: 200 rows in the register offered "Load more",
     which loaded nothing and then vanished. The guess stays only for a
     server that says nothing about its total. */
  const pageTotal = lastPage.data?.total ?? estateTotal;
  const hasMoreLikely =
    pageTotal !== null
      ? allRows.length < pageTotal
      : (lastPage.data?.rows.length ?? 0) === PAGE_SIZE;

  return (
    <article className="max-w-6xl">
      <header className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-semibold">
            {t(`${adapter.i18nPrefix}.list.heading`)}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {t(`${adapter.i18nPrefix}.list.subheading`)}
          </p>
        </div>
        {isAdmin ? (
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={openCreate}
              data-testid="admin-list-create-button"
              className="px-3 py-1.5 rounded bg-brand-brass text-brand-navy text-sm font-medium hover:bg-brand-brassDark"
            >
              {t(`${adapter.i18nPrefix}.list.createButton`)}
            </button>
            {adapter.createNoteKey?.(me.data) && (
              <p
                data-testid="admin-list-create-note"
                className="max-w-xs text-end text-xs text-slate-500"
              >
                {t(adapter.createNoteKey(me.data) ?? "")}
              </p>
            )}
          </div>
        ) : (
          /* Said once, plainly, instead of an affordance that would
             refuse them: reading is theirs, changing is not. */
          <p data-testid="admin-list-read-only" className="text-xs text-slate-500">
            {t("adminList.readOnly")}
          </p>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          dir="auto"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t(`${adapter.i18nPrefix}.list.searchPlaceholder`)}
          data-testid="admin-list-search"
          className="w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <span
          data-testid="admin-list-count"
          className="text-xs text-slate-500 tabular-nums"
        >
          {/* ROUND 13 C4 -- the count of the estate beside the count on
              the screen. The chip read "100 CIs" against a database
              holding 20,000 and nothing qualified it, so the register
              stated a count of the bank. */}
          {/* Round 17 H39 -- "0 CIs" while a search was still on its
              way read as an answer. A search in flight says so. */}
          {search.trim() !== debouncedSearch || firstPage.isLoading
            ? t("modules.foundation.admin.searching")
            : estateTotal !== null && estateTotal > sorted.length
              ? t("modules.foundation.admin.rowCountOfTotal", {
                  shown: sorted.length,
                  total: estateTotal,
                })
              : t(`${adapter.i18nPrefix}.list.rowCount`, { count: sorted.length })}
        </span>
      </div>

      {firstPage.isLoading && (
        <p data-testid="admin-list-data-loading" className="text-sm text-slate-500">
          {t(`${adapter.i18nPrefix}.list.dataLoading`)}
        </p>
      )}
      {firstPage.isError && (
        <p
          data-testid="admin-list-data-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
        >
          {firstError?.detail}
        </p>
      )}

      {firstPage.data && (
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table data-testid="admin-list-table" className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                {adapter.columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={col.sortValue ? () => onSortClick(col.key) : undefined}
                    data-testid={`admin-list-col-${col.key}`}
                    className={`px-3 py-2 text-start font-medium ${
                      col.sortValue
                        ? "cursor-pointer select-none hover:bg-slate-100"
                        : ""
                    }`}
                  >
                    {col.label}
                    {col.sortValue && sort.key === col.key && (
                      <span aria-hidden className="ms-1 text-slate-400">
                        {sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={adapter.rowKey(row)}
                  data-testid="admin-list-row"
                  data-row-id={adapter.rowKey(row)}
                  onClick={isAdmin ? () => openEdit(adapter.rowKey(row)) : undefined}
                  className={
                    "border-t border-slate-100" +
                    (isAdmin ? " cursor-pointer hover:bg-slate-50" : "")
                  }
                >
                  {adapter.columns.map((col) => (
                    <td key={col.key} className="px-3 py-2">
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={adapter.columns.length}
                    data-testid="admin-list-empty"
                    className="px-3 py-6 text-center text-slate-500"
                  >
                    {/* CX round 12 A1 -- "No services match." is a
                        SEARCH answer, and it was shown to a reader who
                        had not searched. On a register with nothing in
                        it that reads as a filter problem, so the
                        Resilience Lead went looking for a filter to
                        clear and found the product had simply never
                        been loaded.

                        `emptyUnfiltered` names what is actually true
                        and, where the register has one, the route. It
                        falls back to the search wording so a register
                        that has not been given the sentence yet is
                        unchanged rather than rendering a missing key. */}
                    {search.trim() === "" ? (
                      /* A6 -- A1 wrote "and, where the register has
                         one, the route", and shipped the sentence
                         without it. The importer is admin-only, so the
                         link is too: a route into a refusal is the
                         dead end this is meant to remove. */
                      <EmptyState
                        testid="admin-list-empty-unfiltered"
                        message={t(`${adapter.i18nPrefix}.list.emptyUnfiltered`, {
                          defaultValue: t(`${adapter.i18nPrefix}.list.empty`),
                        })}
                        cta={
                          adapter.importKind && (me.data?.is_admin ?? false)
                            ? {
                                label: t("modules.foundation.admin.importCta"),
                                to: `/admin/data-import?kind=${adapter.importKind}`,
                              }
                            : undefined
                        }
                        className="mx-auto max-w-xl text-start"
                      />
                    ) : (
                      t(`${adapter.i18nPrefix}.list.empty`)
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {hasMoreLikely && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setPagesLoaded((n) => n + 1)}
            data-testid="admin-list-load-more"
            className="px-3 py-1.5 rounded border border-slate-300 text-sm hover:bg-slate-50"
          >
            {t(`${adapter.i18nPrefix}.list.loadMore`)}
          </button>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        {t(`${adapter.i18nPrefix}.list.auditHint`)}
      </p>

      {(creating || editId) && (
        <AdminEntityDrawer
          adapter={adapter}
          mode={creating ? "create" : "edit"}
          editId={editId}
          onClose={closeDrawer}
        />
      )}
    </article>
  );
}
